// src/components/admin/AdminPanel.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import * as adminApi from '@api/admin';

const AdminPanel = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [domains, setDomains] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  const [loading, setLoading] = useState(true);

  // ---- Edit panel state ----
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editingSubscription, setEditingSubscription] = useState(null);
  const [editForm, setEditForm] = useState({
    user_id: '',
    source_type: 'domain_share',
    routes: '',
    garages: '',
    operators: '',
    tier: 'arrival',
    active: true,
    expires_at: '',
  });
  const [saving, setSaving] = useState(false);

  // ---- Helper to get list from either format ----
  const getValues = (sub, field) => {
    const plural = field;
    const singularMap = { routes: 'route', garages: 'garage_code', operators: 'operator_code' };
    const singular = singularMap[field];
    if (sub[plural] && Array.isArray(sub[plural])) {
      return sub[plural];
    }
    if (sub[singular]) {
      return [sub[singular]];
    }
    return [];
  };

  const fetchData = async (resource) => {
    try {
      if (resource === 'users') return await adminApi.fetchAdminUsers(token);
      if (resource === 'subscriptions') return await adminApi.fetchAdminSubscriptions(token);
      if (resource === 'domains') return await adminApi.fetchAdminDomains(token);
      return [];
    } catch (err) {
      console.error(`Failed to fetch ${resource}`, err);
      return [];
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      const [usersData, subsData, domainsData] = await Promise.all([
        fetchData('users'),
        fetchData('subscriptions'),
        fetchData('domains')
      ]);
      setUsers(usersData);
      setSubscriptions(subsData);
      setDomains(domainsData);
      setLoading(false);
    };
    loadAll();
  }, [token]);

  const toggleAdmin = async (userId) => {
    try {
      await adminApi.toggleAdmin(userId, token);
      const usersData = await fetchData('users');
      setUsers(usersData);
    } catch (err) {
      console.error('Failed to toggle admin', err);
    }
  };

  const openEditPanel = (subscription) => {
    setEditingSubscription(subscription);
    const routes = getValues(subscription, 'routes');
    const garages = getValues(subscription, 'garages');
    const operators = getValues(subscription, 'operators');
    setEditForm({
      user_id: subscription.user_id,
      source_type: subscription.source_type || 'domain_share',
      routes: routes.join(', '),
      garages: garages.join(', '),
      operators: operators.join(', '),
      tier: subscription.tier || 'arrival',
      active: subscription.active !== undefined ? subscription.active : true,
      expires_at: subscription.expires_at ? subscription.expires_at.split('T')[0] : '',
    });
    setShowEditPanel(true);
  };

  const openNewPanel = () => {
    setEditingSubscription(null);
    setEditForm({
      user_id: '',
      source_type: 'domain_share',
      routes: '',
      garages: '',
      operators: '',
      tier: 'arrival',
      active: true,
      expires_at: '',
    });
    setShowEditPanel(true);
  };

  const closePanel = () => {
    setShowEditPanel(false);
    setEditingSubscription(null);
  };

  const handleFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const routes = editForm.routes.split(',').map(s => s.trim()).filter(Boolean);
      const garages = editForm.garages.split(',').map(s => s.trim()).filter(Boolean);
      const operators = editForm.operators.split(',').map(s => s.trim()).filter(Boolean);

      const payload = {
        ...editForm,
        routes,
        garages,
        operators,
        active: editForm.active,
        expires_at: editForm.expires_at || null,
        user_id: parseInt(editForm.user_id),
      };
      if (editingSubscription) {
        payload.id = editingSubscription.id;
      }

      if (editingSubscription) {
        await adminApi.updateSubscription(payload, token);
      } else {
        await adminApi.createSubscription(payload, token);
      }
      const subsData = await fetchData('subscriptions');
      setSubscriptions(subsData);
      closePanel();
      
    } catch (err) {
      console.error('Save error:', err);
      alert('An error occurred while saving.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Loading admin data...</div>;

  return (
    <div style={{ padding: 24, flex: 1, overflow: 'auto' }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, marginBottom: 20 }}>Admin Panel</h1>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 12 }}>
        {['users', 'subscriptions', 'domains'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '8px 16px',
              background: activeTab === tab ? 'var(--accent)' : 'transparent',
              color: activeTab === tab ? 'var(--text-on-accent)' : 'var(--text-secondary)',
              border: activeTab === tab ? 'none' : '1px solid var(--border)',
              borderRadius: 8,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 13,
              transition: 'all 0.15s',
            }}
          >
            {tab.charAt(0).toUpperCase() + tab.slice(1)}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>ID</th>
                <th style={thStyle}>Email</th>
                <th style={thStyle}>Domain</th>
                <th style={thStyle}>Admin</th>
                <th style={thStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(user => (
                <tr key={user.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={tdStyle}>{user.id}</td>
                  <td style={tdStyle}>{user.email}</td>
                  <td style={tdStyle}>{user.company_domain || '-'}</td>
                  <td style={tdStyle}>{user.is_admin ? '✅' : '❌'}</td>
                  <td style={tdStyle}>
                    <button onClick={() => toggleAdmin(user.id)} style={buttonStyle}>Toggle Admin</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Subscriptions Tab */}
      {activeTab === 'subscriptions' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontSize: 14, color: 'var(--text-secondary)' }}>Total: {subscriptions.length}</span>
            <button onClick={openNewPanel} style={{ padding: '6px 16px', background: 'var(--accent)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>+ Add Subscription</button>
          </div>
          <div style={{ maxHeight: 'calc(100vh - 260px)', overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>ID</th>
                  <th style={thStyle}>User ID</th>
                  <th style={thStyle}>Source</th>
                  <th style={thStyle}>Routes</th>
                  <th style={thStyle}>Garages</th>
                  <th style={thStyle}>Operators</th>
                  <th style={thStyle}>Tier</th>
                  <th style={thStyle}>Active</th>
                  <th style={thStyle}>Expires</th>
                  <th style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {subscriptions.map(sub => {
                  const routes = getValues(sub, 'routes');
                  const garages = getValues(sub, 'garages');
                  const operators = getValues(sub, 'operators');
                  return (
                    <tr key={sub.id} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                      <td style={tdStyle}>{sub.id}</td>
                      <td style={tdStyle}>{sub.user_id}</td>
                      <td style={tdStyle}>{sub.source_type}</td>
                      <td style={tdStyle}>{routes.join(', ')}</td>
                      <td style={tdStyle}>{garages.join(', ')}</td>
                      <td style={tdStyle}>{operators.join(', ')}</td>
                      <td style={tdStyle}>{sub.tier}</td>
                      <td style={tdStyle}>{sub.active ? '✅' : '❌'}</td>
                      <td style={tdStyle}>{sub.expires_at ? new Date(sub.expires_at).toLocaleDateString() : '∞'}</td>
                      <td style={tdStyle}>
                        <button onClick={() => openEditPanel(sub)} style={buttonStyle}>Edit</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Domains Tab */}
      {activeTab === 'domains' && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={thStyle}>Domain</th>
                <th style={thStyle}>Operator Name</th>
                <th style={thStyle}>Agency</th>
                <th style={thStyle}>Operator Code</th>
              </tr>
            </thead>
            <tbody>
              {domains.map(d => (
                <tr key={d.domain} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                  <td style={tdStyle}>{d.domain}</td>
                  <td style={tdStyle}>{d.operator_name || '-'}</td>
                  <td style={tdStyle}>{d.operator_agency || '-'}</td>
                  <td style={tdStyle}>{d.operator_code || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Panel Modal */}
      {showEditPanel && (
        <div style={overlayStyle} onClick={closePanel}>
          <div style={panelStyle} onClick={e => e.stopPropagation()}>
            <h2 style={{ fontSize: 20, marginBottom: 16 }}>
              {editingSubscription ? 'Edit Subscription' : 'Add Subscription'}
            </h2>
            <form style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>User ID *</label>
                <input type="number" value={editForm.user_id} onChange={(e) => handleFormChange('user_id', e.target.value)} style={inputStyle} required />
              </div>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>Source Type</label>
                <select value={editForm.source_type} onChange={(e) => handleFormChange('source_type', e.target.value)} style={inputStyle}>
                  <option value="domain_share">Domain Share</option>
                  <option value="individual">Individual</option>
                </select>
              </div>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>Routes (comma separated)</label>
                <input type="text" value={editForm.routes} onChange={(e) => handleFormChange('routes', e.target.value)} placeholder="e.g., 1, 2, 3" style={inputStyle} />
              </div>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>Garages (comma separated)</label>
                <input type="text" value={editForm.garages} onChange={(e) => handleFormChange('garages', e.target.value)} placeholder="e.g., G1, G2" style={inputStyle} />
              </div>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>Operators (comma separated)</label>
                <input type="text" value={editForm.operators} onChange={(e) => handleFormChange('operators', e.target.value)} placeholder="e.g., OP1, OP2" style={inputStyle} />
              </div>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>Tier</label>
                <select value={editForm.tier} onChange={(e) => handleFormChange('tier', e.target.value)} style={inputStyle}>
                  <option value="arrival">Arrival</option>
                  <option value="metrics">Metrics</option>
                </select>
              </div>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>Active</label>
                <input type="checkbox" checked={editForm.active} onChange={(e) => handleFormChange('active', e.target.checked)} style={{ width: 20, height: 20, cursor: 'pointer' }} />
              </div>
              <div style={fieldRowStyle}>
                <label style={labelStyle}>Expiry Date</label>
                <input type="date" value={editForm.expires_at} onChange={(e) => handleFormChange('expires_at', e.target.value)} style={inputStyle} />
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
                <button type="button" onClick={closePanel} style={{ ...buttonStyle, background: 'var(--bg-surface)', color: 'var(--text-secondary)' }}>Cancel</button>
                <button type="button" onClick={handleSave} style={{ ...buttonStyle, background: 'var(--accent)', color: 'var(--text-on-accent)' }} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const thStyle = { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: 'var(--text-muted)', borderBottom: '2px solid var(--border)' };
const tdStyle = { padding: '8px 12px', fontSize: 12, color: 'var(--text-primary)', fontFamily: 'var(--font-mono)' };
const buttonStyle = { padding: '4px 12px', background: 'var(--accent)', color: 'var(--text-on-accent)', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 11, fontWeight: 600 };
const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
const panelStyle = { background: 'var(--bg-elevated)', padding: 24, borderRadius: 12, maxWidth: 600, width: '90%', maxHeight: '90%', overflow: 'auto', boxShadow: '0 8px 30px rgba(0,0,0,0.3)' };
const fieldRowStyle = { display: 'flex', flexDirection: 'column', gap: 4 };
const labelStyle = { fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)' };
const inputStyle = { padding: '6px 10px', fontSize: 13, borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-surface)', color: 'var(--text-primary)', fontFamily: 'inherit' };

export default AdminPanel;